import React, {useState, useEffect} from "react";
import {
  Button,
  TextField,
  Switch,
  Chip,
  MenuItem,
  Select,
  FormControlLabel,
  Tooltip,
} from "@material-ui/core";

import ConfirmDialog from "common_components/confirm_dialog";
import * as InputEditors from "common_components/flowmaster/input_editors";
import Expando from "./expando";
import {llmModels} from "modules/flowmaster/constants/models";

import {WorkflowInput, WorkflowInputId} from "common/flowmaster/types/workflow";

const styles = {
  defaultValueContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: "1em",
    flex: 1,
  },
};

interface InputsProps {
  input: WorkflowInput;
  onUpdateItem: (input: Record<string, string>) => void;
  onDeleteItem: (workflowInputId: WorkflowInputId) => void;
  disabled?: boolean;
  disableBaseInputTypeSelection?: boolean;
}

const RequiresDefaultChip = (
  <Tooltip title="This data asset is a revision of a previous definition with existing configs, so new inputs must provide a default.">
    <Chip style={{color: "red"}} label="Requires Default" size="small" />
  </Tooltip>
);

const IsNewChip = (
  <Tooltip title="This input has been added since the original revision of this data asset.">
    <Chip label="New" size="small" />
  </Tooltip>
);

const Input: React.FC<InputsProps> = ({
  input,
  onUpdateItem,
  onDeleteItem,
  disabled,
  disableBaseInputTypeSelection,
}) => {
  const hasError = input.requires_default && !input.has_default;

  return (
    <Expando key={input.id} error={hasError} beginExpanded={input.is_new}>
      <InputSummary input={input} />
      <InputEditor
        input={input}
        onUpdateItem={onUpdateItem}
        onDeleteItem={onDeleteItem}
        disabled={disabled}
        disableInputTypes={!input.is_new && disableBaseInputTypeSelection}
      />
    </Expando>
  );
};

function InputSummary({input}) {
  return (
    <div style={{display: "flex", alignItems: "center", gap: "0.5em"}}>
      <Chip size="small" label={input.id} />
      {input.is_new && IsNewChip}
      {input.requires_default && !input.has_default && RequiresDefaultChip}
      {input.name}
    </div>
  );
}
function InputEditor({
  input,
  onUpdateItem,
  onDeleteItem,
  disabled,
  disableInputTypes,
}) {
  const [showingDeleteDialog, showDeleteDialog] = useState<boolean>(false);
  const [name, setName] = useState(input.name);
  const [label, setLabel] = useState(input.label);
  const [localDefaultValue, setLocalDefaultValue] = useState<string>(
    input.default_value,
  );

  useEffect(() => {
    setLocalDefaultValue(input.default_value);
  }, [input.default_value]);

  const handleDefaultValueChange = (
    event: React.ChangeEvent<{value: string}>,
  ) => {
    if (input.type === "llm_model") {
      onUpdateItem({default_value: event.target.value});
    } else if (input.type === "schema") {
      setLocalDefaultValue(JSON.stringify(event.target.value));
    } else {
      setLocalDefaultValue(event.target.value);
    }
  };

  const handleDefaultValueBlur = () => {
    if (input.type !== "llm_model") {
      onUpdateItem({default_value: localDefaultValue});
    }
  };

  const handleTypeChange = (event: React.ChangeEvent<{value: string}>) => {
    const newType = event.target.value;
    onUpdateItem({
      type: newType,
      default_value:
        newType === "llm_model"
          ? llmModels[0].key
          : newType === "schema"
          ? JSON.stringify(InputEditors[newType].default)
          : "",
    });
  };

  const renderDefaultValueInput = () => {
    if (!input.has_default) {
      return null;
    }

    const Component = InputEditors[input.type].component;

    const value =
      input.type === "llm_model"
        ? input.default_value
        : input.type === "schema"
        ? JSON.parse(localDefaultValue)
        : localDefaultValue ?? "";

    return (
      <div style={styles.defaultValueContainer}>
        <Component
          input={input}
          value={value}
          handleInputChange={handleDefaultValueChange}
          handleInputBlur={handleDefaultValueBlur}
          style={{width: "30em", marginBottom: "1em"}}
        />
      </div>
    );
  };

  const modelInputMenuItems = Object.entries(InputEditors).map(
    ([key, editor]) => (
      <MenuItem key={key} value={key}>
        {editor.label}
      </MenuItem>
    ),
  );

  const typeSelector = (
    <Select
      value={input.type}
      onChange={handleTypeChange}
      style={{minWidth: "10em", alignSelf: "flex-end", marginLeft: "1em"}}
      disabled={disabled || disableInputTypes}
    >
      {modelInputMenuItems}
    </Select>
  );

  return (
    <fieldset disabled={disabled}>
      <div style={{display: "flex", alignItems: "center", gap: "0.5em"}}>
        <Chip size="small" label={input.id} />
        {input.is_new && IsNewChip}
        {input.requires_default && !input.has_default && RequiresDefaultChip}
        <TextField
          value={name}
          label="Name"
          onChange={event => setName(event.target.value)}
          onBlur={() => onUpdateItem({name})}
          style={{width: "100%"}}
        />
        {!disabled && disableInputTypes ? (
          <Tooltip title="This data asset definition is a revision of a previous version and has existing configurations.">
            {typeSelector}
          </Tooltip>
        ) : (
          <>{typeSelector}</>
        )}
      </div>
      <TextField
        value={label}
        label="Label"
        onChange={event => setLabel(event.target.value)}
        onBlur={() => onUpdateItem({label})}
        style={{width: "100%"}}
      />
      <div style={{marginTop: "10px", display: "flex", alignItems: "center"}}>
        <FormControlLabel
          label="Has Default?"
          control={
            <Switch
              checked={input.has_default}
              onChange={() => {
                onUpdateItem({has_default: !input.has_default});
              }}
            />
          }
        />
        <FormControlLabel
          label="Is Optional?"
          control={
            <Switch
              checked={input.is_optional}
              onChange={() => {
                onUpdateItem({is_optional: !input.is_optional});
              }}
            />
          }
        />
        <FormControlLabel
          label="Is Overrideable?"
          control={
            <Switch
              checked={input.is_overrideable}
              onChange={() => {
                onUpdateItem({is_overrideable: !input.is_overrideable});
              }}
            />
          }
        />
        {renderDefaultValueInput()}
      </div>
      <Button variant="contained" onClick={() => showDeleteDialog(true)}>
        Delete
      </Button>
      <ConfirmDialog
        open={showingDeleteDialog}
        onSuccess={() => onDeleteItem()}
        onClose={() => showDeleteDialog(false)}
        title="Delete Input"
        description="Are you sure you want to delete this input?"
        okButtonCaption="Yes"
        cancelButtonCaption="No"
      />
    </fieldset>
  );
}

export default Input;
